import React from 'react';
import { GridFour, List, RowsPlusBottom, ArrowsHorizontal } from '@phosphor-icons/react';

/**
 * Reusable layout toggle bar.
 * Modes: 'grid' (responsive cards) | 'horizontal' (scroll snap row) |
 *        'vertical' (single column full width cards) | 'list' (compact table-like rows)
 *
 * Usage:
 *   const [layout, setLayout] = useLayoutMode('contacts');
 *   <LayoutToggle layout={layout} setLayout={setLayout} />
 */
export const LayoutToggle = ({ layout, setLayout, modes, testidPrefix = 'layout' }) => {
  const allModes = modes || [
    { value: 'horizontal', label: 'Horizontal', Icon: ArrowsHorizontal },
    { value: 'grid', label: 'Grille', Icon: GridFour },
    { value: 'vertical', label: 'Vertical', Icon: RowsPlusBottom },
    { value: 'list', label: 'Liste', Icon: List },
  ];
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', background: 'white', border: '1px solid #E2E2E6', borderRadius: 8, padding: 3 }} data-testid={`${testidPrefix}-toggle`}>
      {allModes.map(m => {
        const active = layout === m.value;
        const I = m.Icon;
        return (
          <button
            key={m.value}
            type="button"
            data-testid={`${testidPrefix}-${m.value}-btn`}
            onClick={() => setLayout(m.value)}
            title={m.label}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', borderRadius: 6, border: 'none',
              background: active ? '#002FA7' : 'transparent',
              color: active ? 'white' : '#374151',
              cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: '0.78rem',
              transition: 'all 0.15s',
            }}
          >
            <I size={14} weight={active ? 'fill' : 'regular'} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Persisted layout mode hook (localStorage).
 * Default: 'grid' if not provided.
 */
export const useLayoutMode = (key, defaultMode = 'grid') => {
  const storageKey = `layout_mode_${key}`;
  const [value, setValue] = React.useState(() => {
    try { return localStorage.getItem(storageKey) || defaultMode; } catch { return defaultMode; }
  });
  const update = React.useCallback((v) => {
    setValue(v);
    try { localStorage.setItem(storageKey, v); } catch { /* ignore */ }
  }, [storageKey]);
  return [value, update];
};

/**
 * Wrapper container that applies layout-specific styles around a list of children.
 */
export const LayoutContainer = ({ layout, children, testid }) => {
  const styles = {
    horizontal: { display: 'flex', gap: 16, overflowX: 'auto', overflowY: 'visible', padding: '4px 4px 16px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
    vertical: { display: 'flex', flexDirection: 'column', gap: 12 },
    list: { display: 'flex', flexDirection: 'column', gap: 4 },
  };
  return <div style={styles[layout] || styles.grid} data-testid={testid || `container-${layout}`}>{children}</div>;
};

/**
 * Returns inline style + className for a list/card container based on layout mode.
 *   getLayoutContainerProps('grid')      → { style: { display:'grid', ... } }
 *   getLayoutContainerProps('horizontal')→ { style: { display:'flex', overflowX:'auto', ... } }
 *   getLayoutContainerProps('vertical')  → { style: { display:'flex', flexDirection:'column' } }
 *   getLayoutContainerProps('list')      → { style: ..., className: 'layout-mode-list' }
 *
 * The optional cardMinWidth controls the responsive grid breakpoint.
 */
export const getLayoutContainerProps = (layout, options = {}) => {
  const { cardMinWidth = '320px', horizontalCardWidth = '320px' } = options;
  switch (layout) {
    case 'horizontal':
      return {
        style: { display: 'flex', gap: 16, overflowX: 'auto', overflowY: 'visible', padding: '4px 4px 16px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' },
        cardExtraStyle: { flex: `0 0 ${horizontalCardWidth}`, scrollSnapAlign: 'start' },
      };
    case 'vertical':
      return {
        style: { display: 'flex', flexDirection: 'column', gap: 14 },
        className: 'layout-mode-vertical',
        cardExtraStyle: { width: '100%' },
      };
    case 'list':
      return {
        style: { display: 'flex', flexDirection: 'column', gap: 4 },
        className: 'layout-mode-list',
      };
    case 'grid':
    default:
      return {
        style: { display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}, 1fr))`, gap: 16 },
      };
  }
};

export default LayoutToggle;
